<template>
  <div class="gammes-product row">
    <h2 class="text-center title-prod mb-2">{{ $t("gamme_product.title") }}</h2>
    <p>{{ $t("gamme_product.subtitle") }}</p>
    <div class="d-flex gamme-list">
      <label
        v-for="gamme in gammes"
        class="round mx-2 px-4 py-1"
        :class="{ active: gamme.name == CurrentGammeName }"
        :key="gamme.id"
        @click.prevent="ChangeGamme(gamme)"
      >
        {{ gamme.name }}
        <img v-if="gamme.name == CurrentGammeName" :src="iconchecked" />
      </label>
    </div>
    <div class="col-12 col-md-11 col-lg-11 carousel-container">
      <div v-if="uniqueProducts.length > 0" class="carousel__wrapper">
        <Carousel v-bind="carouselConfig">
          <Slide v-for="product in uniqueProducts" :key="`product-${product.id}`">
            <ProductCardSlide :product="normalizeProduct(product)" />
          </Slide>
          <template #addons>
            <Navigation />
          </template>
        </Carousel>
      </div>
      <div v-else-if="!isLoading" class="alert alert-info">Aucun produit trouvé pour cette gamme</div>
    </div>
    <h6 class="show-products my-5">
      <router-link to="/dashboard/products">{{ $t("gamme_product.see_all") }}</router-link>
    </h6>
  </div>
</template>

<script>
  import ProductCardSlide from "./products/ProductCardSlide.vue";
  import iconchecked from "../assets/checked.png";
  import "vue3-carousel/carousel.css";
  import { Carousel, Slide, Navigation } from "vue3-carousel";
  import { useGammesStore } from "@/stores/gammesStore";

  export default {
    name: "GammesProduct",
    components: {
      ProductCardSlide,
      Carousel,
      Slide,
      Navigation,
    },
    props: {
      GammeProduct: {
        type: Array,
        required: true,
        default: () => [],
      },
    },
    data() {
      return {
        iconchecked,
        CurrentGammeName: "",
        gammesStore: null,
        gammes: [],
        isLoading: false,
      };
    },
    computed: {
      filteredProducts() {
        if (!this.CurrentGammeName) return this.GammeProduct;

        const selectedGamme = this.gammes.find(g => g.name === this.CurrentGammeName);
        if (!selectedGamme) return this.GammeProduct;

        return this.filterByGamme(selectedGamme);
      },
      uniqueProducts() {
        // Ensure we only show each product once, regardless of variants
        const seen = new Set();
        return this.filteredProducts.filter(product => {
          if (seen.has(product.id)) return false;
          seen.add(product.id);
          return true;
        });
      },
      carouselConfig() {
        const shouldLoop = this.uniqueProducts.length >= 4;
        return {
          itemsToShow: 4,
          gap: 40,
          snapAlign: "start",
          autoplay: 4000,
          autoplayHoverPause: true,
          loop: shouldLoop, // Enable loop only if 4 or more products
          wrapAround: shouldLoop, // Enable wrapAround only if 4 or more products
          pauseAutoplayOnHover: true,
          breakpoints: {
            0: { itemsToShow: 1, snapAlign: "start", loop: shouldLoop, wrapAround: shouldLoop },
            400: { itemsToShow: 2, snapAlign: "start", loop: shouldLoop, wrapAround: shouldLoop },
            600: { itemsToShow: 2, snapAlign: "start", loop: shouldLoop, wrapAround: shouldLoop },
            992: { itemsToShow: 4, snapAlign: "start", loop: shouldLoop, wrapAround: shouldLoop },
          },
        };
      },
    },
    methods: {
      normalizeProduct(product) {
        // Ensure consistent product structure for the card
        return {
          ...product,
          variant: product.variant || (product.variantsDetails && product.variantsDetails[0]) || {},
        };
      },
      filterByGamme(gamme) {
        const taxonCodes = this.getTaxonCodes(gamme);
        return this.GammeProduct.filter(product => {
          const productTaxon = this.extractTaxonCode(product.mainTaxonSupplyvet);
          return productTaxon && taxonCodes.includes(productTaxon);
        });
      },
      getTaxonCodes(gamme) {
        const codes = [gamme.code];
        if (gamme.childrenDetails) {
          gamme.childrenDetails.forEach(child => codes.push(child.code));
        }
        return codes;
      },
      extractTaxonCode(taxon) {
        if (!taxon) return null;
        const parts = taxon.split("/");
        return parts[parts.length - 1];
      },
      ChangeGamme(gamme) {
        this.isLoading = true;
        this.CurrentGammeName = this.CurrentGammeName === gamme.name ? "" : gamme.name;
        setTimeout(() => {
          this.isLoading = false;
        }, 100);
      },
    },
    async created() {
      this.gammesStore = useGammesStore();
      await this.gammesStore.fetchGammes();
      this.gammes = this.gammesStore.gammes;
    },
  };
</script>

<style scoped>
  .gammes-product {
    gap: 10px;
    margin-top: 20px;
    justify-content: center;
  }

  .title-prod {
    color: #1e347d;
    font-weight: 900;
    line-height: 55px;
    font-size: 45px;
    font-style: normal;
    justify-content: center;
  }

  .gamme-list {
    justify-content: center;
  }

  .round {
    border: 1px solid #1e347d;
    border-radius: 76px;
    color: #1e347d;
    cursor: pointer;
  }

  .round.active {
    background: #1e347d;
    color: white;
  }

  @media (max-width: 768px) {
    .title-prod {
      font-size: 14px;
      line-height: 30px;
    }
  }

  .show-products a {
    color: #1e347d;
    font-size: 16px;
    font-weight: bold;
    text-decoration: underline !important;
  }

  .carousel {
    --vc-nav-background: rgba(255, 255, 255, 0.7);
    --vc-nav-border-radius: 100%;
  }

  .carousel__wrapper {
    padding: 2px;
    position: relative;
    margin: 0 auto;
  }

  .carousel-container {
    padding: 0 15px;
  }

  :deep(.carousel__icon) {
    fill: #1e347d;
    height: 70%;
  }

  :deep(.carousel__next),
  :deep(.carousel__prev) {
    top: 38%;
    border: 1px solid #1e347d !important;
    background: #eff1f7;
  }

  :deep(.carousel__next:hover),
  :deep(.carousel__prev:hover) {
    background: #c9cfde;
  }

  :deep(.carousel__prev) {
    left: -4% !important;
  }

  :deep(.carousel__next) {
    right: -4% !important;
  }

  /* Responsive styles */
  @media (max-width: 992px) {
    .gammes-product {
      margin-top: 10px;
      gap: 6px;
      padding-left: 8px;
      padding-right: 8px;
    }
    .title-prod {
      font-size: 22px;
      line-height: 32px;
      margin-bottom: 10px;
    }
    .gamme-list {
      flex-wrap: wrap;
      gap: 6px;
    }
    .round {
      font-size: 13px;
      padding: 4px 12px;
    }
    .carousel__wrapper {
      padding: 0;
      max-width: 100vw;
    }
    .carousel-container {
      padding: 0 8px;
    }
  }

  @media (max-width: 600px) {
    .gammes-product {
      margin-top: 4px;
      gap: 3px;
      padding-left: 2px;
      padding-right: 2px;
    }
    .title-prod {
      font-size: 15px;
      line-height: 22px;
      margin-bottom: 6px;
    }
    .gamme-list {
      flex-wrap: wrap;
      gap: 10px;
    }
    .round {
      font-size: 11px;
      padding: 2px 6px;
    }
    .carousel__wrapper {
      padding: 0;
      max-width: 100vw;
      margin: 0 12px;
    }
    .carousel-container {
      padding: 0 8px;
    }
    .show-products a {
      font-size: 13px;
    }
    
    /* Styles ajoutés pour réduire l'espacement du carrousel sur mobile */
    :deep(.carousel__slide) {
      padding: 0 6px;
    }

    
  }

  @media (max-width: 480px) {
    .carousel__wrapper {
      margin: 0 16px; /* Marges encore plus importantes sur très petits écrans */
    }
    
    :deep(.carousel__slide) {
      padding: 0 4px;
    }
    
    :deep(.carousel__next),
    :deep(.carousel__prev) {
      transform: scale(0.8);
    }

  
  }
</style>
